// src/pages/StrategiesPage.js
import React, { useEffect, useMemo, useState } from 'react';
import './StrategiesPage.css';
import TopBar from '../components/TopBar';
import Tooltip from '../components/Tooltip';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getUserStrategies,
  getPublicStrategies,
  createStrategy,
  updateStrategyMeta,
  deleteStrategy,
  setStrategyVisibility,
  getOrCreateMyShareCode,
  resolveShareCode,
  addStrategyShareByUid,
  removeStrategyShareByUid,
  getStrategiesSharedWithMe,
} from '../firebase/strategyService';
import { getUserDisplayNamesByIds } from '../firebase/usersService';
import { containsBlasphemy } from '../utils/blasphemyFilter';
import {
  NotebookPen,
  Plus,
  Sparkles,
  Loader2,
  AlertTriangle,
  X,
  ArrowRight,
  Pencil,
  Trash2,
  Tag,
  Award,
  StickyNote,
  Clock3,
  Layers,
  Lock,
  Globe2,
  Eye,
  UserRound,
  Users,
  Copy,
  Check,
  KeyRound,
} from 'lucide-react';

function formatRelativeTime(ts) {
  if (!ts) return 'Mai modificata';
  const diff = Date.now() - Number(ts);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Adesso';
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ore fa`;
  const days = Math.floor(hours / 24);
  return `${days} giorni fa`;
}

function countKeys(obj) {
  return Object.keys(obj || {}).length;
}

function StrategyCard({ strategy, onOpen, onRename, onDelete, onToggleVisibility, onShare, toggling }) {
  const categoriesCount = countKeys(strategy.categories);
  const tiersCount = countKeys(strategy.tiers);
  const notesCount = countKeys(strategy.notes);
  const taggedPlayers = new Set([
    ...Object.keys(strategy.categories || {}),
    ...Object.keys(strategy.tiers || {}),
    ...Object.keys(strategy.notes || {}),
  ]).size;
  const isPublic = strategy.visibility === 'public';
  const sharedCount = Array.isArray(strategy.sharedWith) ? strategy.sharedWith.length : 0;

  return (
    <article className="strat-card glass">
      <div className="strat-card__head">
        <div className="strat-card__heading">
          <div className="strat-card__iconBadge">
            <NotebookPen size={18} />
          </div>
          <div className="strat-card__headingText">
            <h3>{strategy.name}</h3>
          </div>
        </div>

        <div className="strat-card__headRight">
          <div className="strat-card__headRightTop">
            <Tooltip as="span" className="strat-card__updatedAt" content="Ultimo aggiornamento">
              <Clock3 size={11} />
              {formatRelativeTime(strategy.updatedAt || strategy.createdAt)}
            </Tooltip>
            <Tooltip
              as="span"
              className={`strat-card__visibility strat-card__visibility--${isPublic ? 'public' : 'private'}`}
              content={isPublic ? 'Pubblica · visibile a tutti gli utenti' : 'Privata · visibile solo a te'}
            >
              {isPublic ? <Globe2 size={12} /> : <Lock size={12} />}
              {isPublic ? 'Pubblica' : 'Privata'}
            </Tooltip>
            {!isPublic && sharedCount > 0 && (
              <Tooltip
                as="span"
                className="strat-card__visibility strat-card__visibility--shared"
                content={`Condivisa con ${sharedCount} ${sharedCount === 1 ? 'persona' : 'persone'}`}
              >
                <Users size={12} />
                {sharedCount}
              </Tooltip>
            )}
          </div>

          <div className="strat-card__actions">
            <Tooltip
              as="button"
              className="strat-card__iconBtn"
              type="button"
              onClick={(e) => { e.stopPropagation(); onShare(strategy); }}
              content="Condividi con persone specifiche"
            >
              <Users size={16} />
            </Tooltip>
            <Tooltip
              as="button"
              className="strat-card__iconBtn"
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleVisibility(strategy); }}
              disabled={toggling}
              content={isPublic ? 'Rendi privata' : 'Rendi pubblica'}
            >
              {toggling ? <Loader2 size={16} className="spin" /> : isPublic ? <Globe2 size={16} /> : <Lock size={16} />}
            </Tooltip>
            <Tooltip
              as="button"
              className="strat-card__iconBtn"
              type="button"
              onClick={(e) => { e.stopPropagation(); onRename(strategy); }}
              content="Rinomina strategia"
            >
              <Pencil size={16} />
            </Tooltip>
            <Tooltip
              as="button"
              className="strat-card__iconBtn strat-card__iconBtn--danger"
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(strategy); }}
              content="Elimina strategia"
            >
              <Trash2 size={16} />
            </Tooltip>
          </div>
        </div>
      </div>

      {strategy.description && <p className="strat-card__desc">{strategy.description}</p>}

      <div className="strat-card__metaChips">
        <Tooltip as="span" className="strat-card__metaChip" content="Giocatori con etichetta">
          <Tag size={13} />
          <strong>{categoriesCount}</strong> Etichette
        </Tooltip>
        <Tooltip as="span" className="strat-card__metaChip" content="Giocatori con fascia">
          <Award size={13} />
          <strong>{tiersCount}</strong> Fasce
        </Tooltip>
        <Tooltip as="span" className="strat-card__metaChip" content="Giocatori con nota">
          <StickyNote size={13} />
          <strong>{notesCount}</strong> Note
        </Tooltip>
      </div>

      <div className="strat-card__footer">
        <span className="strat-card__playersCount">
          {taggedPlayers === 0 ? 'Ancora nessun giocatore taggato' : `${taggedPlayers} giocatori coinvolti`}
        </span>

        <button className="strat-card__open" type="button" onClick={() => onOpen(strategy.id)}>
          Apri
          <ArrowRight size={16} />
        </button>
      </div>
    </article>
  );
}

// Card di sola lettura per il registro pubblico (publicStrategies): niente
// azioni di modifica/eliminazione/visibilità, solo nome, host, descrizione
// e un tasto "Vedi" verso la vista read-only della strategia altrui.
function PublicStrategyCard({ entry, ownerName, onOpen, variant = 'public' }) {
  const isShared = variant === 'shared';
  return (
    <article className="strat-card glass strat-card--public">
      <div className="strat-card__head">
        <div className="strat-card__heading">
          <div className="strat-card__iconBadge strat-card__iconBadge--public">
            {isShared ? <Users size={18} /> : <Globe2 size={18} />}
          </div>
          <div className="strat-card__headingText">
            <h3>{entry.name || 'Strategia senza nome'}</h3>
          </div>
        </div>

        <div className="strat-card__headRight">
          <div className="strat-card__headRightTop">
            <Tooltip as="span" className="strat-card__updatedAt" content="Ultimo aggiornamento">
              <Clock3 size={11} />
              {formatRelativeTime(entry.updatedAt)}
            </Tooltip>
            <span className={`strat-card__visibility strat-card__visibility--${isShared ? 'shared' : 'public'}`}>
              {isShared ? <Users size={12} /> : <Globe2 size={12} />}
              {isShared ? 'Condivisa' : 'Pubblica'}
            </span>
          </div>
        </div>
      </div>

      <div className="strat-card__host">
        <UserRound size={13} />
        <span>Creata da <strong>{ownerName || 'Utente'}</strong></span>
      </div>

      {entry.description && <p className="strat-card__desc">{entry.description}</p>}

      <div className="strat-card__footer">
        <span className="strat-card__playersCount">
          {isShared ? 'Puoi modificarla e importarla in un’asta' : 'Sola lettura — importabile in un’asta'}
        </span>

        <button className="strat-card__open" type="button" onClick={() => onOpen(entry)}>
          {isShared ? <Pencil size={16} /> : <Eye size={16} />}
          {isShared ? 'Apri' : 'Vedi'}
        </button>
      </div>
    </article>
  );
}

function VisibilityChoice({ value, onChange }) {
  return (
    <div className="strat-visibility-grid">
      <button
        type="button"
        className={`strat-visibility-option ${value === 'private' ? 'is-active' : ''}`}
        onClick={() => onChange('private')}
      >
        <Lock size={16} />
        <div>
          <strong>Privata</strong>
          <small>Visibile solo dal tuo account</small>
        </div>
      </button>
      <button
        type="button"
        className={`strat-visibility-option ${value === 'public' ? 'is-active' : ''}`}
        onClick={() => onChange('public')}
      >
        <Globe2 size={16} />
        <div>
          <strong>Pubblica</strong>
          <small>Importabile da tutti gli utenti</small>
        </div>
      </button>
    </div>
  );
}

function StrategyFormModal({ mode, initialName, initialDescription, initialVisibility, onClose, onSubmit, saving }) {
  const [name, setName] = useState(initialName || '');
  const [description, setDescription] = useState(initialDescription || '');
  const [visibility, setVisibility] = useState(initialVisibility || 'private');
  const [attempted, setAttempted] = useState(false);

  const isBlasphemous = containsBlasphemy(name);
  const isValid = name.trim().length > 0 && !isBlasphemous;

  const handleSubmit = (e) => {
    e.preventDefault();
    setAttempted(true);
    if (!isValid) return;
    onSubmit({ name: name.trim(), description: description.trim(), visibility });
  };

  return (
    <div className="strat-modal-backdrop" onClick={onClose}>
      <div className="strat-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="strat-modal__head">
          <div>
            <div className="strat-modal__eyebrow">
              <Sparkles size={14} />
              {mode === 'edit' ? 'Rinomina strategia' : 'Nuova strategia'}
            </div>
            <h2>{mode === 'edit' ? 'Aggiorna nome e descrizione' : 'Dai un nome alla tua strategia'}</h2>
            <p>Etichette, fasce e note le imposti dopo, dal dettaglio della strategia.</p>
          </div>
          <button className="strat-modal__close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <form className="strat-form" onSubmit={handleSubmit}>
          <label className={isBlasphemous ? 'is-invalid' : ''}>
            <span>Nome strategia</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Puntare sui giovani"
              autoFocus
              required
            />
            {isBlasphemous && <small className="strat-form__error">Nome non consentito.</small>}
          </label>

          <label>
            <span>Descrizione (opzionale)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Es. Fasce prezzo aggressive, punto su chi ha meno di 24 anni"
              rows={3}
            />
          </label>

          {mode !== 'edit' && (
            <label>
              <span>Visibilità</span>
              <VisibilityChoice value={visibility} onChange={setVisibility} />
            </label>
          )}

          {attempted && !isValid && !isBlasphemous && (
            <div className="strat-form__notice">
              <AlertTriangle size={14} />
              <span>Il nome della strategia è obbligatorio.</span>
            </div>
          )}

          <div className="strat-form__actions">
            <button type="button" className="strat-cancel-btn" onClick={onClose}>
              Annulla
            </button>
            <button className="strat-create-btn" type="submit" disabled={saving}>
              {saving ? (
                <><Loader2 size={16} className="spin" /> Salvo...</>
              ) : mode === 'edit' ? (
                <>Salva modifiche</>
              ) : (
                <><Plus size={16} /> Crea strategia</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteStrategyOverlay({ strategy, onClose, onConfirm, loading }) {
  if (!strategy) return null;
  return (
    <div className="strat-modal-backdrop" onClick={onClose}>
      <div className="strat-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="strat-modal__head">
          <div>
            <div className="strat-modal__eyebrow">
              <AlertTriangle size={14} />
              Elimina strategia
            </div>
            <h2>Confermi l'eliminazione?</h2>
            <p>
              Stai per cancellare <strong>{strategy.name}</strong> e tutte le etichette, fasce e note salvate al suo interno.
            </p>
          </div>
          <button className="strat-modal__close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <div className="strat-delete-box">
          <strong>Operazione irreversibile</strong>
          <span>Le aste in cui è già stata importata non vengono toccate: i dati copiati restano lì.</span>
        </div>

        <div className="strat-form__actions">
          <button type="button" className="strat-cancel-btn" onClick={onClose}>
            Annulla
          </button>
          <button type="button" className="strat-delete-confirm-btn" onClick={onConfirm} disabled={loading}>
            {loading ? (
              <><Loader2 size={16} className="spin" /> Elimino...</>
            ) : (
              <><Trash2 size={16} /> Elimina strategia</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modale "Condividi con persone specifiche": mostra chi ha già accesso,
// permette di aggiungerne di nuovi col loro codice amico e di rimuoverli.
function StrategyShareModal({ strategy, ownerUid, myShareCode, onClose, onChanged }) {
  const [working, setWorking] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState('');
  const [namesByUid, setNamesByUid] = useState({});
  const [copied, setCopied] = useState(false);

  const sharedWith = useMemo(
    () => (Array.isArray(strategy.sharedWith) ? strategy.sharedWith.map(String) : []),
    [strategy.sharedWith]
  );

  useEffect(() => {
    let cancelled = false;
    if (!sharedWith.length) { setNamesByUid({}); return undefined; }
    getUserDisplayNamesByIds(sharedWith)
      .then((names) => { if (!cancelled) setNamesByUid(names); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sharedWith]);

  const handleCopyCode = async () => {
    if (!myShareCode) return;
    try {
      await navigator.clipboard.writeText(myShareCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard non disponibile */ }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    const code = codeInput.trim().toUpperCase();
    if (code.length < 4) { setError('Inserisci il codice completo dell’amico.'); return; }
    setWorking(true);
    try {
      const targetUid = await resolveShareCode(code);
      if (!targetUid) { setError('Codice non valido: nessun utente trovato.'); return; }
      const next = await addStrategyShareByUid(ownerUid, strategy, targetUid);
      setCodeInput('');
      onChanged({ ...strategy, sharedWith: next || sharedWith });
    } catch (err) {
      if (err?.message === 'own-uid') setError('Questo è il tuo codice: non puoi condividere con te stesso.');
      else if (err?.message === 'limit') setError('Hai raggiunto il numero massimo di persone per una strategia.');
      else setError('Non è stato possibile aggiungere questa persona. Riprova.');
    } finally {
      setWorking(false);
    }
  };

  const handleRemove = async (granteeUid) => {
    setError('');
    setWorking(true);
    try {
      const next = await removeStrategyShareByUid(ownerUid, strategy, granteeUid);
      onChanged({ ...strategy, sharedWith: next || sharedWith.filter((u) => u !== granteeUid) });
    } catch {
      setError('Rimozione non riuscita. Riprova.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="strat-modal-backdrop" onClick={onClose}>
      <div className="strat-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="strat-modal__head">
          <div>
            <div className="strat-modal__eyebrow">
              <Users size={14} />
              Condividi strategia
            </div>
            <h2>{strategy.name}</h2>
            <p>
              Le persone che aggiungi qui possono vedere e importare questa strategia,
              anche se resta privata. Non possono modificarla.
            </p>
          </div>
          <button className="strat-modal__close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <div className="strat-share-mycode">
          <KeyRound size={14} />
          <span>Il tuo codice</span>
          <strong>{myShareCode || '······'}</strong>
          <button type="button" onClick={handleCopyCode} disabled={!myShareCode}>
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? 'Copiato' : 'Copia'}
          </button>
        </div>

        <form className="strat-share-add" onSubmit={handleAdd}>
          <label>
            <span>Aggiungi una persona col suo codice</span>
            <div className="strat-share-add__row">
              <input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="Es. K7QP2M"
                maxLength={8}
                autoFocus
              />
              <button type="submit" className="strat-create-btn" disabled={working}>
                {working ? <Loader2 size={15} className="spin" /> : <Plus size={15} />}
                Aggiungi
              </button>
            </div>
          </label>
          {error && (
            <div className="strat-form__notice">
              <AlertTriangle size={14} />
              <span>{error}</span>
            </div>
          )}
        </form>

        <div className="strat-share-list">
          <span className="strat-share-list__title">
            Hanno accesso ({sharedWith.length})
          </span>
          {sharedWith.length ? (
            sharedWith.map((granteeUid) => (
              <div key={granteeUid} className="strat-share-person">
                <UserRound size={14} />
                <span>{namesByUid[granteeUid] || 'Utente'}</span>
                <button
                  type="button"
                  className="strat-share-person__remove"
                  onClick={() => handleRemove(granteeUid)}
                  disabled={working}
                >
                  <X size={13} />
                </button>
              </div>
            ))
          ) : (
            <p className="strat-share-list__empty">
              Ancora nessuno. Chiedi all’amico il suo codice (lo trova qui, nella stessa schermata).
            </p>
          )}
        </div>

        <div className="strat-form__actions">
          <button type="button" className="strat-cancel-btn" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StrategiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [strategies, setStrategies] = useState([]);
  const [publicStrategies, setPublicStrategies] = useState([]);
  const [publicLoading, setPublicLoading] = useState(true);
  const [ownerNamesByUid, setOwnerNamesByUid] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [showPublicStrategies, setShowPublicStrategies] = useState(false);
  const [sharingStrategy, setSharingStrategy] = useState(null);
  const [myShareCode, setMyShareCode] = useState('');
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [sharedWithMeLoading, setSharedWithMeLoading] = useState(true);
  const [sharedOwnerNames, setSharedOwnerNames] = useState({});
  const [showSharedWithMe, setShowSharedWithMe] = useState(false);
  const [copiedHeroCode, setCopiedHeroCode] = useState(false);

  const loadStrategies = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const list = await getUserStrategies(user.uid);
      setStrategies(list);
    } finally {
      setLoading(false);
    }
  };

  const loadSharedWithMe = async () => {
    if (!user?.uid) return;
    setSharedWithMeLoading(true);
    try {
      const list = await getStrategiesSharedWithMe(user.uid).catch(() => []);
      setSharedWithMe(list);
      const names = await getUserDisplayNamesByIds(list.map((e) => e.ownerUid)).catch(() => ({}));
      setSharedOwnerNames(names);
    } finally {
      setSharedWithMeLoading(false);
    }
  };

  useEffect(() => {
    loadStrategies().catch(() => setLoading(false));
  }, [user?.uid]);

  // Codice personale di condivisione: generato/recuperato una volta all'ingresso.
  useEffect(() => {
    if (!user?.uid) return;
    getOrCreateMyShareCode(user.uid).then((code) => { if (code) setMyShareCode(code); }).catch(() => {});
  }, [user?.uid]);

  // Strategie che altri hanno condiviso con me.
  useEffect(() => {
    loadSharedWithMe().catch(() => setSharedWithMeLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const handleCopyHeroCode = async () => {
    if (!myShareCode) return;
    try {
      await navigator.clipboard.writeText(myShareCode);
      setCopiedHeroCode(true);
      window.setTimeout(() => setCopiedHeroCode(false), 1600);
    } catch { /* clipboard non disponibile */ }
  };

  // Riflette in locale un cambto di condivisione fatto nella modale, senza
  // ricaricare tutto da Firestore.
  const handleShareChanged = (updatedStrategy) => {
    setStrategies((prev) => prev.map((s) => (s.id === updatedStrategy.id ? updatedStrategy : s)));
    setSharingStrategy((cur) => (cur && cur.id === updatedStrategy.id ? updatedStrategy : cur));
  };

  // Strategie pubbliche di ALTRI utenti (le proprie, anche se pubbliche,
  // restano solo in "Le mie strategie" qui sopra). Nome host risolto a
  // parte via users/{uid} — leggibile da chiunque sia autenticato.
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      setPublicLoading(true);
      try {
        const list = await getPublicStrategies().catch(() => []);
        const others = list.filter((entry) => String(entry.ownerUid) !== String(user.uid));
        if (cancelled) return;
        setPublicStrategies(others);
        const names = await getUserDisplayNamesByIds(others.map((entry) => entry.ownerUid)).catch(() => ({}));
        if (!cancelled) setOwnerNamesByUid(names);
      } finally {
        if (!cancelled) setPublicLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const stats = useMemo(() => {
    const taggedPlayersTotal = strategies.reduce((acc, s) => {
      const ids = new Set([
        ...Object.keys(s.categories || {}),
        ...Object.keys(s.tiers || {}),
        ...Object.keys(s.notes || {}),
      ]);
      return acc + ids.size;
    }, 0);
    return { total: strategies.length, taggedPlayersTotal };
  }, [strategies]);

  const handleCreate = async ({ name, description, visibility }) => {
    if (!user?.uid) return;
    setSaving(true);
    try {
      const created = await createStrategy(user.uid, { name, description, visibility });
      setShowCreate(false);
      navigate(`/strategie/${created.id}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async ({ name, description }) => {
    if (!user?.uid || !editingStrategy?.id) return;
    setSaving(true);
    try {
      await updateStrategyMeta(user.uid, editingStrategy, { name, description });
      setEditingStrategy(null);
      await loadStrategies();
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (strategy) => {
    if (!user?.uid || !strategy?.id) return;
    setTogglingId(strategy.id);
    try {
      const next = strategy.visibility === 'public' ? 'private' : 'public';
      await setStrategyVisibility(user.uid, strategy, next);
      await loadStrategies();
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!user?.uid || !pendingDelete?.id) return;
    setDeleting(true);
    try {
      await deleteStrategy(user.uid, pendingDelete.id);
      setPendingDelete(null);
      await loadStrategies();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="container strategies-page">
      <TopBar />

      <section className="strategies-hero glass">
        <div className="strategies-hero__copy">
          <div className="strategies-hero__eyebrow">
            <NotebookPen size={16} />
            Le mie strategie
          </div>

          <h1>Le tue strategie</h1>

          <p>
            Preset di etichette, fasce di livello e note personali legati al tuo account: crea una strategia
            qui, poi importala dentro qualsiasi asta.
          </p>
        </div>

        <div className="strategies-hero__aside">
          <div className="strategies-stats-panel glass">
            <div className="strategies-stats-panel__total">
              <div className="strategies-stats-panel__totalIcon">
                <Layers size={20} />
              </div>
              <div>
                <strong>{stats.total}</strong>
                <span>Strategie salvate</span>
              </div>
            </div>
            <div className="strategies-stats-panel__item">
              <Tag size={14} />
              <strong>{stats.taggedPlayersTotal}</strong>
              <span>Giocatori taggati in totale</span>
            </div>
          </div>

          <div className="strategies-hero__cta">
            <div className="strategies-mycode glass">
              <KeyRound size={14} />
              <span>Il tuo codice condivisione</span>
              <strong>{myShareCode || '······'}</strong>
              <Tooltip
                as="button"
                type="button"
                onClick={handleCopyHeroCode}
                disabled={!myShareCode}
                content="Dallo a un amico perché possa condividere le sue strategie con te"
              >
                {copiedHeroCode ? <Check size={13} /> : <Copy size={13} />}
                {copiedHeroCode ? 'Copiato' : 'Copia'}
              </Tooltip>
            </div>

            <button className="strat-create-btn strat-create-btn--hero" onClick={() => setShowCreate(true)} type="button">
              <Plus size={18} />
              Nuova strategia
            </button>
          </div>
        </div>
      </section>

      <section className="strategies-list-section">
        <h2 className="strategies-section-title">Le mie strategie</h2>
        {loading ? (
          <div className="strat-empty-box glass">Caricamento strategie...</div>
        ) : strategies.length ? (
          <div className="strategies-list-grid">
            {strategies.map((strategy) => (
              <StrategyCard
                key={strategy.id}
                strategy={strategy}
                onOpen={(id) => navigate(`/strategie/${id}`)}
                onRename={setEditingStrategy}
                onDelete={setPendingDelete}
                onToggleVisibility={handleToggleVisibility}
                onShare={setSharingStrategy}
                toggling={togglingId === strategy.id}
              />
            ))}
          </div>
        ) : (
          <div className="strat-empty-box glass">
            Non hai ancora creato nessuna strategia. Premi "Nuova strategia" per iniziare.
          </div>
        )}
      </section>

      <section className="strategies-list-section">
        <button
          type="button"
          className={`strategies-public-toggle ${showSharedWithMe ? 'is-active' : ''}`}
          onClick={() => setShowSharedWithMe((v) => !v)}
        >
          <Users size={16} />
          {showSharedWithMe ? 'Nascondi condivise con me' : 'Condivise con me'}
          {!sharedWithMeLoading && (
            <span className="strategies-public-toggle__count">{sharedWithMe.length}</span>
          )}
        </button>

        {showSharedWithMe && (
          <div className="strategies-public-panel">
            <h2 className="strategies-section-title">
              <Users size={16} />
              Condivise con me
            </h2>
            {sharedWithMeLoading ? (
              <div className="strat-empty-box glass">Caricamento…</div>
            ) : sharedWithMe.length ? (
              <div className="strategies-list-grid">
                {sharedWithMe.map((entry) => (
                  <PublicStrategyCard
                    key={entry.id}
                    entry={entry}
                    variant="shared"
                    ownerName={sharedOwnerNames[entry.ownerUid]}
                    onOpen={(e) => navigate(`/strategie/pubblica/${e.ownerUid}/${e.strategyId || e.id}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="strat-empty-box glass">
                Nessuna strategia condivisa con te. Dai il tuo codice a un amico perché ti aggiunga alle sue.
              </div>
            )}
          </div>
        )}
      </section>

      <section className="strategies-list-section">
        <button
          type="button"
          className={`strategies-public-toggle ${showPublicStrategies ? 'is-active' : ''}`}
          onClick={() => setShowPublicStrategies((v) => !v)}
        >
          <Globe2 size={16} />
          {showPublicStrategies ? 'Nascondi strategie pubbliche' : 'Mostra strategie pubbliche'}
          {!publicLoading && (
            <span className="strategies-public-toggle__count">{publicStrategies.length}</span>
          )}
        </button>

        {showPublicStrategies && (
          <div className="strategies-public-panel">
            <h2 className="strategies-section-title">
              <Globe2 size={16} />
              Strategie pubbliche
            </h2>
            {publicLoading ? (
              <div className="strat-empty-box glass">Caricamento strategie pubbliche...</div>
            ) : publicStrategies.length ? (
              <div className="strategies-list-grid">
                {publicStrategies.map((entry) => (
                  <PublicStrategyCard
                    key={entry.id}
                    entry={entry}
                    ownerName={ownerNamesByUid[entry.ownerUid]}
                    onOpen={(e) => navigate(`/strategie/pubblica/${e.ownerUid}/${e.strategyId || e.id}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="strat-empty-box glass">
                Nessuna strategia pubblica al momento — quando un altro utente ne rende pubblica una, la trovi qui.
              </div>
            )}
          </div>
        )}
      </section>

      <footer className="strategies-footer">
        <span>FantApp! - All rights reserved - Sorgenti giocatori </span>
        <a href="https://www.fantamaster.it/" target="_blank" rel="noreferrer">
          FantaMaster
        </a>
      </footer>

      {showCreate && (
        <StrategyFormModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          saving={saving}
        />
      )}

      {editingStrategy && (
        <StrategyFormModal
          mode="edit"
          initialName={editingStrategy.name}
          initialDescription={editingStrategy.description}
          onClose={() => setEditingStrategy(null)}
          onSubmit={handleRename}
          saving={saving}
        />
      )}

      {sharingStrategy && (
        <StrategyShareModal
          strategy={sharingStrategy}
          ownerUid={user?.uid}
          myShareCode={myShareCode}
          onClose={() => setSharingStrategy(null)}
          onChanged={handleShareChanged}
        />
      )}

      {pendingDelete && (
        <DeleteStrategyOverlay
          strategy={pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
          loading={deleting}
        />
      )}
    </main>
  );
}
